import { SFUClient } from "./SFUClient";
import AwaitingSFUCallbacks from "./AwaitingSFUCallbacks";
import { TrafficAccelerator, } from "gather-video-common/dist/src/public/types";
import { ConsumerState } from "../../ConsumerInfo";
import { Logger } from "../../Logger";
import isEqual from "lodash/isEqual";
import { deferredPromise } from "gather-common/dist/src/public/promises";
import { gatherVideoClientDataSource, metricsManager as globalsMetricsManager, } from "../../Globals";
import { GatedFeatName } from "gather-client-common/dist/src/public/gatekeeping/FEATURES";
export const SFU_CLIENT_DELETION_TIMEOUT_MS = 0;
export const BACKUP_SFU_LIFESPAN_MS = 4000;
export class SFUClientManager {
    spaceId;
    playerId;
    externalEventEmitter;
    subscribeSFUAssignment;
    unsubscribeSFUAssignment;
    playerConnectedSFU;
    playerLeavingSFU;
    stopped = true;
    destroyed = false;
    subscribed = {};
    awaitingSFUCallbacks = new AwaitingSFUCallbacks();
    idToSFUAddr = {};
    idToSFU = {};
    sfuCleanupTimeouts = {};
    _sfuClients = {};
    rtcStatsManager;
    onlyCloudflareTurn;
    constructor(id, spaceId, gvcEventEmitter, subscribeSFUAssignment, unsubscribeSFUAssignment, playerConnectedSFU, playerLeavingSFU, rtcStatsManager) {
        this.playerId = id;
        this.spaceId = spaceId;
        this.externalEventEmitter = gvcEventEmitter;
        this.subscribeSFUAssignment = subscribeSFUAssignment;
        this.unsubscribeSFUAssignment = unsubscribeSFUAssignment;
        this.playerConnectedSFU = playerConnectedSFU;
        this.playerLeavingSFU = playerLeavingSFU;
        this.rtcStatsManager = rtcStatsManager;
        const enableTwilio = gatherVideoClientDataSource.gate(GatedFeatName.EnableTwilioTurn, this.playerId, this.spaceId);
        this.onlyCloudflareTurn = !enableTwilio;
    }
    destroy() {
        this.destroyed = true;
        this.awaitingSFUCallbacks.clear();
        Object.values(this.sfuCleanupTimeouts).forEach((timeoutID) => clearTimeout(timeoutID));
        this.sfuCleanupTimeouts = {};
        Object.entries(this.idToSFU).forEach(([playerId, assignment]) => {
            const primarySFU = assignment.primarySFU;
            this.playerLeavingSFU({ playerId, sfuClient: primarySFU });
            const backupSFU = assignment.backupSFU;
            if (backupSFU) {
                this.playerLeavingSFU({ playerId, sfuClient: backupSFU });
                if (assignment.clearBackupSFUTimeout) {
                    clearTimeout(assignment.clearBackupSFUTimeout);
                    delete assignment.clearBackupSFUTimeout;
                }
                delete assignment.backupSFU;
            }
        });
        this.idToSFU = {};
        this.subscribed = {};
        Object.values(this._sfuClients).forEach((sfuClient) => {
            sfuClient.destroy();
        });
        this._sfuClients = {};
    }
    addPlayer(playerId, tagSet) {
        if (this.destroyed)
            return;
        if (isEqual(this.subscribed[playerId], tagSet))
            return;
        this.subscribed[playerId] = tagSet;
        const sfuClients = this.getSFUsByPlayerId(playerId);
        if (sfuClients.length === 0 && !this.stopped) {
            this.subscribeSFUAssignment(playerId);
            return;
        }
        if (sfuClients.length > 0) {
            sfuClients.forEach((sfuClient) => {
                this._cancelSFUCleanup(sfuClient.serverURL);
            });
        }
    }
    removePlayer(playerId) {
        if (this.destroyed)
            return;
        if (!this.subscribed[playerId])
            return;
        delete this.subscribed[playerId];
        if (!this.stopped) {
            this.unsubscribeSFUAssignment(playerId);
        }
        this.awaitingSFUCallbacks.deleteCallbackOfPlayer(playerId);
        this._deleteFromIdToSFU(playerId);
        delete this.idToSFUAddr[playerId];
    }
    async updatePlayerSFUAssignment(srcId, sfuAddr) {
        if (this.destroyed)
            return undefined;
        this.idToSFUAddr[srcId] = sfuAddr;
        globalsMetricsManager.addMetricReliably("sfu-client-manager", {
            action: "assign",
            sfuAddr,
            playerId: srcId,
            timestamp: Date.now(),
        });
        if (srcId === this.playerId) {
            globalsMetricsManager.setAttribute("cloudProvider", sfuAddr.includes("do.gather.town")
                ? "do"
                : sfuAddr.includes("goog.gather.town")
                    ? "gcp"
                    : sfuAddr.includes("aws.gather.town")
                        ? "aws"
                        : "unknown");
        }
        this.awaitingSFUCallbacks.deleteCallbackOfPlayer(srcId);
        const isConnectedToSFU = Boolean(this._sfuClients[sfuAddr]);
        if (isConnectedToSFU) {
            this._reconcileSFUs(srcId, sfuAddr);
            return this._sfuClients[sfuAddr];
        }
        const isConnectingToSFU = this.awaitingSFUCallbacks.isAwaitingSFU(sfuAddr);
        const { promise: pendingSFUClientPromise, resolve: pendingSFUClientResolve, reject: pendingSFUClientReject, } = deferredPromise();
        this.awaitingSFUCallbacks.addCallback(sfuAddr, srcId, () => {
            this._reconcileSFUs(srcId, sfuAddr);
        }, isConnectingToSFU ? pendingSFUClientResolve : undefined, isConnectedToSFU ? pendingSFUClientReject : undefined);
        if (isConnectingToSFU) {
            try {
                await pendingSFUClientPromise;
                return this._sfuClients[sfuAddr];
            }
            catch {
                return undefined;
            }
        }
        Logger.log("initializing", sfuAddr);
        const sfuClient = this._createSFUClient(sfuAddr);
        try {
            const enableCloudflareTurn = gatherVideoClientDataSource.gate(GatedFeatName.EnableCloudflareTurn, this.playerId, this.spaceId);
            const preferCloudflareTurn = gatherVideoClientDataSource.gate(GatedFeatName.PreferCloudflareTurn, this.playerId, this.spaceId);
            const options = {
                forceTurn: false,
                trafficAccelerator: TrafficAccelerator.GlobalAccelerator,
                cloudflareTurn: enableCloudflareTurn,
                preferCloudflareTurn,
            };
            let sessionOptions = {};
            try {
                const iceTransportRequestOptions = window.sessionStorage.getItem("gather_turn_settings") || "{}";
                sessionOptions = JSON.parse(iceTransportRequestOptions);
            }
            catch (e) {
            }
            const turnProvider = this.onlyCloudflareTurn ? "cloudflare" : undefined;
            await sfuClient.start(sfuAddr, srcId, this.spaceId, { ...options, ...sessionOptions }, () => { }, turnProvider);
        }
        catch (e) {
            Logger.warn(`SFU start failed: player ${srcId} is on ${sfuAddr}`);
        }
        try {
            await sfuClient.initialStartPromise;
        }
        catch (e) {
            globalsMetricsManager.addMetricReliably("sfu-client-manager", {
                action: "aborted",
                sfuAddr,
                playerId: srcId,
                timestamp: Date.now(),
            });
            this.awaitingSFUCallbacks.cancelAwaiting(sfuAddr);
            sfuClient.destroy();
            return undefined;
        }
        globalsMetricsManager.addMetricReliably("sfu-client-manager", {
            action: "connected",
            sfuAddr,
            playerId: srcId,
            hasCallbacks: String(this.awaitingSFUCallbacks.hasCallbacks(sfuAddr)),
            timestamp: Date.now(),
        });
        const playerHasBeenReassigned = !this.awaitingSFUCallbacks.hasCallbacks(sfuAddr);
        this._sfuClients[sfuAddr] = sfuClient;
        this.awaitingSFUCallbacks.doneAwaiting(sfuAddr);
        if (!this.isSubscribedToPlayer(srcId) || playerHasBeenReassigned) {
            this._scheduleSFUCleanup(srcId, sfuAddr);
            return undefined;
        }
        return this._sfuClients[sfuAddr];
    }
    start() {
        this.stopped = false;
        Logger.log(`[SFUClientManager] queueing state sync to router, currently ${Object.keys(this.subscribed).length} subscribed.`);
        this.subscribeSFUAssignment(this.playerId);
        for (const id in this.subscribed) {
            this.subscribeSFUAssignment(id);
        }
    }
    stop() {
        this.stopped = true;
    }
    isSubscribedToPlayer(playerId) {
        return this.subscribed[playerId] || playerId === this.playerId;
    }
    getSubscribedTagsByPlayerId(playerId) {
        return this.subscribed[playerId];
    }
    getSubscribedPlayers() {
        return Object.keys(this.subscribed);
    }
    get sfuClients() {
        return this._sfuClients;
    }
    getSFUByAddr(sfuAddr) {
        return this._sfuClients[sfuAddr];
    }
    getPrimarySFUByPlayerId(playerId) {
        return this.idToSFU[playerId]?.primarySFU;
    }
    getLatestSFUAddrByPlayerId(playerId) {
        return (this.awaitingSFUCallbacks.playerToSFUAddr(playerId) ??
            this.getPrimarySFUByPlayerId(playerId)?.serverURL);
    }
    getSFUsByPlayerId(playerId) {
        const assignment = this.idToSFU[playerId];
        if (!assignment)
            return [];
        const sfus = [assignment.primarySFU];
        if (assignment.backupSFU) {
            sfus.push(assignment.backupSFU);
        }
        return sfus;
    }
    getPlayersBySFUAddr(sfuAddr) {
        return Object.keys(this.idToSFU)
            .filter((id) => [this.idToSFU[id]?.primarySFU.serverURL, this.idToSFU[id]?.backupSFU?.serverURL].includes(sfuAddr))
            .map((id) => ({
            primary: this.idToSFU[id]?.primarySFU.serverURL,
            backup: this.idToSFU[id]?.backupSFU?.serverURL,
            id,
        }));
    }
    onLocalTracksProduced(id, sfuAddr) {
        const assignment = this.idToSFU[id];
        const sfuClient = this.sfuClients[sfuAddr];
        if (!sfuClient || !assignment)
            return;
        if (assignment.backupSFU && assignment.primarySFU === sfuClient) {
            assignment.backupSFU.produceMigrationCompleted();
        }
    }
    onInfo(sfuClient, info) {
        const unneededBackupSFUs = [];
        Object.entries(info).forEach(([id, idInfo]) => {
            if (!this.subscribed[id])
                return;
            const assignment = this.idToSFU[id];
            if (!assignment || ![assignment.primarySFU, assignment.backupSFU].includes(sfuClient))
                return;
            if (assignment.backupSFU && assignment.primarySFU === sfuClient) {
                if (idInfo.state !== ConsumerState.Connected)
                    return;
                unneededBackupSFUs.push(id);
            }
        });
        unneededBackupSFUs.forEach((id) => this._clearBackupSFU(id, false));
    }
    requestServerDebug(srcIds, srcStreamId) {
        Object.values(this._sfuClients).forEach((sfuClient) => {
            sfuClient.requestSFUDebug(srcIds, srcStreamId);
        });
    }
    _createSFUClient(sfuAddr) {
        const sfuClient = new SFUClient(this.externalEventEmitter, this.rtcStatsManager);
        const hasNoCallbacks = () => !this.awaitingSFUCallbacks.hasCallbacks(sfuAddr);
        sfuClient.shouldCancelInitialStart = hasNoCallbacks;
        sfuClient.onWebSocketFailedToConnect = (additionalContext) => {
            const debugContext = { ...additionalContext, idToSFUAddr: Object.entries(this.idToSFUAddr) };
            Logger.warn(`error in sfu initialization on ${sfuAddr}: ${JSON.stringify(debugContext)}`);
            globalsMetricsManager.addMetricReliably("gvc-sfu-connect-error", {
                sfuAddr,
                error: debugContext.error,
                message: debugContext.message,
                failedCount: debugContext.failedCount,
            });
        };
        return sfuClient;
    }
    _deleteFromIdToSFU(playerId) {
        this._clearBackupSFU(playerId, false);
        this._clearPrimarySFU(playerId);
        delete this.idToSFU[playerId];
    }
    _sfuClientCanBeDestroyed(playerId, sfuAddress) {
        if (this.destroyed)
            return true;
        const sfuClient = this._sfuClients[sfuAddress];
        const otherPlayersOnSFU = Object.entries(this.idToSFU).filter(([id, assignment]) => id !== playerId && [assignment.primarySFU, assignment.backupSFU].includes(sfuClient));
        return otherPlayersOnSFU.length === 0;
    }
    _scheduleSFUCleanup(playerId, sfuAddress) {
        if (this._sfuClientCanBeDestroyed(playerId, sfuAddress) &&
            this.sfuCleanupTimeouts[sfuAddress] == null) {
            globalsMetricsManager.addMetricReliably("sfu-client-manager", {
                action: "schedule-cleanup",
                sfuAddr: sfuAddress,
                playerId,
                timestamp: Date.now(),
            });
            this.sfuCleanupTimeouts[sfuAddress] = window.setTimeout(() => {
                delete this.sfuCleanupTimeouts[sfuAddress];
                if (this._sfuClientCanBeDestroyed(playerId, sfuAddress)) {
                    globalsMetricsManager.addMetricReliably("sfu-client-manager", {
                        action: "cleanup",
                        sfuAddr: sfuAddress,
                        playerId,
                        timestamp: Date.now(),
                    });
                    this._sfuClients[sfuAddress]?.destroy();
                    delete this._sfuClients[sfuAddress];
                }
            }, SFU_CLIENT_DELETION_TIMEOUT_MS);
        }
    }
    _cancelSFUCleanup(sfuAddress) {
        if (this.sfuCleanupTimeouts[sfuAddress]) {
            globalsMetricsManager.addMetricReliably("sfu-client-manager", {
                action: "cancel-cleanup",
                sfuAddr: sfuAddress,
                timestamp: Date.now(),
            });
            clearTimeout(this.sfuCleanupTimeouts[sfuAddress]);
            delete this.sfuCleanupTimeouts[sfuAddress];
        }
    }
    _setNewPrimarySFU = (id, newSFU) => {
        const timestamp = new Date().toString();
        const assignment = this.idToSFU[id];
        if (!assignment) {
            this.idToSFU[id] = { primarySFU: newSFU, timestamp };
            return;
        }
        this._clearBackupSFU(id);
        assignment.backupSFU = assignment.primarySFU;
        assignment.primarySFU = newSFU;
        if (id === this.playerId) {
            assignment.backupSFU.produceMigrationStart();
        }
        globalsMetricsManager.addMetricReliably("sfu-client-manager", {
            action: "update",
            primary: assignment.primarySFU.serverURL,
            backup: assignment.backupSFU.serverURL,
            playerId: id,
            timestamp: Date.now(),
        });
        assignment.timestamp = timestamp;
        assignment.clearBackupSFUTimeout = window.setTimeout(() => this._clearBackupSFU(id), BACKUP_SFU_LIFESPAN_MS);
    };
    _clearBackupSFU = (playerId, updateConsumerInfo = true) => {
        const assignment = this.idToSFU[playerId];
        if (!assignment)
            return;
        const backupSFU = assignment.backupSFU;
        if (!backupSFU)
            return;
        clearTimeout(assignment.clearBackupSFUTimeout);
        delete assignment.clearBackupSFUTimeout;
        delete assignment.backupSFU;
        this.playerLeavingSFU({
            playerId,
            sfuClient: backupSFU,
            updateConsumerInfo,
            consumerInfo: assignment.primarySFU.info[playerId],
        });
        this._scheduleSFUCleanup(playerId, backupSFU.serverURL);
    };
    _clearPrimarySFU(playerId) {
        const primarySFU = this.idToSFU[playerId]?.primarySFU;
        if (primarySFU && primarySFU.serverURL) {
            this.playerLeavingSFU({ playerId, sfuClient: primarySFU });
            this._scheduleSFUCleanup(playerId, primarySFU?.serverURL);
        }
    }
    _reconcileSFUs(srcId, sfuAddr) {
        const oldSFUAddress = this.idToSFU[srcId]?.primarySFU.serverURL;
        if (oldSFUAddress === sfuAddr)
            return;
        if (!this.isSubscribedToPlayer(srcId))
            return;
        const newSFUClient = this._sfuClients[sfuAddr];
        if (!newSFUClient) {
            Logger.error("SFU client missing in _reconcileSFUs");
            return;
        }
        this._setNewPrimarySFU(srcId, newSFUClient);
        this._cancelSFUCleanup(sfuAddr);
        this.playerConnectedSFU({ playerId: srcId, sfuClient: newSFUClient });
    }
    _summarizeIdToSFU() {
        const idToSFU = {};
        Object.entries(this.idToSFU).forEach(([id, { timestamp, primarySFU, backupSFU }]) => {
            idToSFU[id] = {
                primarySFU: primarySFU.serverURL,
                ...(timestamp && { timestamp }),
                ...(backupSFU && { backupSFU: backupSFU.serverURL }),
            };
        });
        return idToSFU;
    }
    networkChanged() {
        Object.values(this._sfuClients).forEach((sfuClient) => {
            sfuClient.reload(true);
        });
    }
    summarizeSubscribed() {
        const subscribed = {};
        Object.entries(this.subscribed).forEach(([id, tags]) => {
            subscribed[id] = Array.from(tags);
        });
        return subscribed;
    }
    async summarize() {
        const sfuClients = {};
        await Promise.all(Object.entries(this._sfuClients).map(async ([id, sfuClient]) => {
            const summary = await sfuClient.summarize();
            sfuClients[id] = summary;
        }));
        return {
            idToSFU: this._summarizeIdToSFU(),
            sfuClients,
            sfusPendingCleanup: Object.keys(this.sfuCleanupTimeouts),
            awaitingSFU: this.awaitingSFUCallbacks.summarize(),
        };
    }
    async getConnectionReport(userId) {
        const report = {};
        const assignment = this.idToSFU[userId];
        report["sfuAddr"] = assignment?.primarySFU.serverURL ?? "no connected SFU";
        report["sfuAddrUpdated"] = assignment?.timestamp ?? "never";
        if (assignment?.backupSFU !== undefined) {
            report["backupSFUAddr"] = assignment.backupSFU.serverURL;
        }
        const awaitingSFU = this.awaitingSFUCallbacks.playerToSFUAddr(userId);
        if (awaitingSFU) {
            report["awaitingNewSFU"] = awaitingSFU;
        }
        const subs = this.subscribed[userId] ?? new Set();
        report["subscribedKinds"] = [...subs].join(" ");
        const sfuReport = await assignment?.primarySFU.getConnectionReport(userId);
        Object.entries(sfuReport ?? {}).forEach(([key, value]) => {
            report[`sfu.${key}`] = value;
        });
        return report;
    }
}
//# sourceMappingURL=SFUClientManager.js.map